import React, { useState, useEffect, useCallback, useRef } from 'react';
import Editor, { Monaco, OnMount } from '@monaco-editor/react';
import FuturisticFileExplorer, { FileSystemEntry as ExplorerFileSystemEntry } from './FuturisticFileExplorer';
import './FuturisticCodeEditor.scss';
import commonService from '@/services/common.service';

// Simple SVG for Close icon
const CloseIcon = ({ onClick }: { onClick: (e: React.MouseEvent) => void }) => (
  <svg onClick={onClick} viewBox="0 0 24 24" width="16" height="16" fill="currentColor" style={{ cursor: 'pointer', marginLeft: '8px', verticalAlign: 'middle' }}>
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
);

const theme = {
  "base": "vs-dark",
  "inherit": true,
  "rules": [
    {
      "background": "141414",
      "token": ""
    },
    {
      "foreground": "5f5a60",
      "fontStyle": "italic",
      "token": "comment"
    },
    {
      "foreground": "cf6a4c",
      "token": "constant"
    },
    {
      "foreground": "9b703f",
      "token": "entity"
    },
    {
      "foreground": "cda869",
      "token": "keyword"
    },
    {
      "foreground": "f9ee98",
      "token": "storage"
    },
    {
      "foreground": "8f9d6a",
      "token": "string"
    },
    {
      "foreground": "9b859d",
      "token": "support"
    },
    {
      "foreground": "7587a6",
      "token": "variable"
    },
    {
      "foreground": "d2a8a1",
      "fontStyle": "italic underline",
      "token": "invalid.deprecated"
    },
    {
      "foreground": "f8f8f8",
      "background": "562d56bf",
      "token": "invalid.illegal"
    },
    {
      "background": "b0b3ba14",
      "token": "text source"
    },
    {
      "background": "b1b3ba21",
      "token": "text.html.ruby source"
    },
    {
      "foreground": "9b5c2e",
      "fontStyle": "italic",
      "token": "entity.other.inherited-class"
    },
    {
      "foreground": "daefa3",
      "token": "string source"
    },
    {
      "foreground": "ddf2a4",
      "token": "string constant"
    },
    {
      "foreground": "e9c062",
      "token": "string.regexp"
    },
    {
      "foreground": "cf7d34",
      "token": "string.regexp constant.character.escape"
    },
    {
      "foreground": "cf7d34",
      "token": "string.regexp source.ruby.embedded"
    },
    {
      "foreground": "cf7d34",
      "token": "string.regexp string.regexp.arbitrary-repitition"
    },
    {
      "foreground": "8a9a95",
      "token": "string variable"
    },
    {
      "foreground": "dad085",
      "token": "support.function"
    },
    {
      "foreground": "cf6a4c",
      "token": "support.constant"
    },
    {
      "foreground": "8996a8",
      "token": "meta.preprocessor.c"
    },
    {
      "foreground": "afc4db",
      "token": "meta.preprocessor.c keyword"
    },
    {
      "foreground": "494949",
      "token": "meta.tag.sgml.doctype"
    },
    {
      "foreground": "494949",
      "token": "meta.tag.sgml.doctype entity"
    },
    {
      "foreground": "494949",
      "token": "meta.tag.sgml.doctype string"
    },
    {
      "foreground": "494949",
      "token": "meta.tag.preprocessor.xml"
    },
    {
      "foreground": "494949",
      "token": "meta.tag.preprocessor.xml entity"
    },
    {
      "foreground": "494949",
      "token": "meta.tag.preprocessor.xml string"
    },
    {
      "foreground": "ac885b",
      "token": "declaration.tag"
    },
    {
      "foreground": "ac885b",
      "token": "declaration.tag entity"
    },
    {
      "foreground": "ac885b",
      "token": "meta.tag"
    },
    {
      "foreground": "ac885b",
      "token": "meta.tag entity"
    },
    {
      "foreground": "e0c589",
      "token": "declaration.tag.inline"
    },
    {
      "foreground": "e0c589",
      "token": "declaration.tag.inline entity"
    },
    {
      "foreground": "e0c589",
      "token": "source entity.name.tag"
    },
    {
      "foreground": "e0c589",
      "token": "source entity.other.attribute-name"
    },
    {
      "foreground": "e0c589",
      "token": "meta.tag.inline"
    },
    {
      "foreground": "e0c589",
      "token": "meta.tag.inline entity"
    },
    {
      "foreground": "cda869",
      "token": "meta.selector.css entity.name.tag"
    },
    {
      "foreground": "8f9d6a",
      "token": "meta.selector.css entity.other.attribute-name.tag.pseudo-class"
    },
    {
      "foreground": "8b98ab",
      "token": "meta.selector.css entity.other.attribute-name.id"
    },
    {
      "foreground": "9b703f",
      "token": "meta.selector.css entity.other.attribute-name.class"
    },
    {
      "foreground": "c5af75",
      "token": "support.type.property-name.css"
    },
    {
      "foreground": "f9ee98",
      "token": "meta.property-group support.constant.property-value.css"
    },
    {
      "foreground": "f9ee98",
      "token": "meta.property-value support.constant.property-value.css"
    },
    {
      "foreground": "8693a5",
      "token": "meta.preprocessor.at-rule keyword.control.at-rule"
    },
    {
      "foreground": "ca7840",
      "token": "meta.property-value support.constant.named-color.css"
    },
    {
      "foreground": "ca7840",
      "token": "meta.property-value constant"
    },
    {
      "foreground": "8f9d6a",
      "token": "meta.constructor.argument.css"
    },
    {
      "foreground": "f8f8f8",
      "background": "0e2231",
      "fontStyle": "italic",
      "token": "meta.diff"
    },
    {
      "foreground": "f8f8f8",
      "background": "0e2231",
      "fontStyle": "italic",
      "token": "meta.diff.header"
    },
    {
      "foreground": "f8f8f8",
      "background": "0e2231",
      "fontStyle": "italic",
      "token": "meta.separator"
    },
    {
      "foreground": "f8f8f8",
      "background": "420e09",
      "token": "markup.deleted"
    },
    {
      "foreground": "f8f8f8",
      "background": "4a410d",
      "token": "markup.changed"
    },
    {
      "foreground": "f8f8f8",
      "background": "253b22",
      "token": "markup.inserted"
    },
    {
      "foreground": "f9ee98",
      "token": "markup.list"
    },
    {
      "foreground": "cf6a4c",
      "token": "markup.heading"
    }
  ],
  "colors": {
    "editor.foreground": "#F8F8F8",
    "editor.background": "#141414",
    "editor.selectionBackground": "#DDF0FF33",
    "editor.lineHighlightBackground": "#FFFFFF08",
    "editorCursor.foreground": "#A7A7A7",
    "editorWhitespace.foreground": "#FFFFFF40"
  }
}

interface OpenFile {
  path: string;
  // id could be path, or a unique generated id if paths can change/not unique enough
}

interface FuturisticCodeEditorProps {
  initialFileTree: ExplorerFileSystemEntry[];
  initialFileContents: Record<string, string>; // All known file contents
  onFileContentChange?: (filePath: string, newContent: string) => void;
  projectId: string;
}

const FuturisticCodeEditor: React.FC<FuturisticCodeEditorProps> = ({
  initialFileTree,
  initialFileContents,
  onFileContentChange,
  projectId,
}) => {
  const [openFiles, setOpenFiles] = useState<OpenFile[]>([]);
  const [activeFilePath, setActiveFilePath] = useState<string | null>(null);
  const [currentFileContent, setCurrentFileContent] = useState<string>('');
  const editorRef = useRef<Parameters<OnMount>[0] | null>(null);
  const monacoRef = useRef<Monaco | null>(null);

  // Effect to load content when activeFilePath or initialFileContents change
  useEffect(() => {
    const newContent = activeFilePath ? initialFileContents[activeFilePath] || '' : '';
    setCurrentFileContent(newContent);

    if (editorRef.current && activeFilePath) {
      if (editorRef.current.getValue() !== newContent) {
        editorRef.current.setValue(newContent);
      }

      if (monacoRef.current && editorRef.current.getModel()) {
        const model = editorRef.current.getModel();
        if (model) {
          const newLanguage = getLanguage(activeFilePath);
          const currentLanguage = model.getLanguageId();
          if (newLanguage !== currentLanguage) {
            monacoRef.current.editor.setModelLanguage(model, newLanguage);
          }
        }
      }
    }
  }, [activeFilePath, initialFileContents]);

  // Effect to set a default open file if none are open (e.g., on initial load)
  useEffect(() => {
    if (openFiles.length === 0 && initialFileTree.length > 0 && !activeFilePath) {
      const findFirstFile = (nodes: ExplorerFileSystemEntry[]): string | null => {
        for (const node of nodes) {
          if (node.type === 'file') return node.path;
          if (node.children) {
            const found = findFirstFile(node.children);
            if (found) return found;
          }
        }
        return null;
      };
      const firstFile = findFirstFile(initialFileTree);
      if (firstFile) {
        setOpenFiles([{ path: firstFile }]);
        setActiveFilePath(firstFile);
      }
    }
  }, [initialFileTree, openFiles.length, activeFilePath]);


  const handleFileSelect = useCallback((path: string) => {
    if (!openFiles.find(f => f.path === path)) {
      setOpenFiles(prevOpenFiles => [...prevOpenFiles, { path }]);
    }
    commonService.handleMixpanelTrack("file_selected", {
      project_id: projectId,
      file_path: path,
      timestamp: new Date().toISOString()
    });
    setActiveFilePath(path);
  }, [openFiles, projectId]);

  const handleTabClick = (path: string) => {
    setActiveFilePath(path);
  };

  const handleCloseTab = (path: string, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent tab click from firing

    const fileIndex = openFiles.findIndex(f => f.path === path);
    if (fileIndex === -1) return;

    const newOpenFiles = openFiles.filter(f => f.path !== path);
    setOpenFiles(newOpenFiles);

    if (activeFilePath === path) {
      if (newOpenFiles.length > 0) {
        // Try to activate tab to the right, or the last one if closing the last in list
        const nextActiveIndex = Math.min(fileIndex, newOpenFiles.length - 1);
        setActiveFilePath(newOpenFiles[nextActiveIndex].path);
      } else {
        setActiveFilePath(null); // No tabs left
      }
    }
  };

  const handleCodeChange = (value: string | undefined) => {
    if (activeFilePath && value !== undefined) {
      setCurrentFileContent(value);
      if (onFileContentChange) {
        onFileContentChange(activeFilePath, value);
      }
      // Also update initialFileContents directly for current session consistency
      // This assumes onFileContentChange will eventually persist this to the source of initialFileContents
      if (Object.prototype.hasOwnProperty.call(initialFileContents, activeFilePath)) {
        initialFileContents[activeFilePath] = value;
      }
    }
  };

  // Handle keyboard shortcuts for save functionality
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ctrl+S or Cmd+S to save changes
      if ((event.ctrlKey || event.metaKey) && event.key === 's') {
        event.preventDefault();
        
        // Import CommonService dynamically to avoid circular dependencies
        import('@/services/common.service').then(({ default: CommonService }) => {
          // Trigger commit dialog via communication service
          CommonService._communications.triggerCommitDialogSubject.next(true);
        });
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const getLanguage = (filePath: string | null): string => {
    if (!filePath) return 'plaintext';
    const extension = filePath.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'js': case 'jsx': return 'javascript';
      case 'ts': case 'tsx': return 'typescript';
      case 'css': return 'css';
      case 'scss': return 'scss';
      case 'json': return 'json';
      case 'html': return 'html';
      case 'md': return 'markdown';
      case 'py': return 'python';
      default: return 'plaintext';
    }
  };

  return (
    <div className="futuristic-code-editor-layout">
      <FuturisticFileExplorer
        fileTree={initialFileTree}
        onFileSelect={handleFileSelect}
        activeFile={activeFilePath}
      />
      <div className="editor-pane">
        <div className="file-tabs">
          {openFiles.map(file => (
            <div
              key={file.path}
              className={`tab ${file.path === activeFilePath ? 'active' : ''}`}
              onClick={() => handleTabClick(file.path)}
            >
              <span className="tab-name">{file.path.split('/').pop()}</span>
              <CloseIcon onClick={(e) => handleCloseTab(file.path, e)} />
            </div>
          ))}
        </div>
        {activeFilePath ? (
          <Editor
            key={`${activeFilePath}-${editorRef.current}`}
            height="calc(100% - 35px)"
            language={getLanguage(activeFilePath)}
            theme="dracula"
            value={currentFileContent}
            onChange={handleCodeChange}
            onMount={(editor, monaco: Monaco) => {
              editorRef.current = editor;
              monacoRef.current = monaco;

              // Configure TypeScript/JavaScript compiler options for JSX/TSX
              monaco.languages.typescript.typescriptDefaults.setCompilerOptions({
                target: monaco.languages.typescript.ScriptTarget.Latest,
                allowNonTsExtensions: true,
                moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
                module: monaco.languages.typescript.ModuleKind.CommonJS,
                noEmit: true,
                esModuleInterop: true,
                jsx: monaco.languages.typescript.JsxEmit.React,
                reactNamespace: "React",
                allowJs: true,
                typeRoots: ["node_modules/@types"],
                lib: ["dom", "dom.iterable", "es6"]
              });

              monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
                target: monaco.languages.typescript.ScriptTarget.Latest,
                allowNonTsExtensions: true,
                moduleResolution: monaco.languages.typescript.ModuleResolutionKind.NodeJs,
                module: monaco.languages.typescript.ModuleKind.CommonJS,
                noEmit: true,
                esModuleInterop: true,
                jsx: monaco.languages.typescript.JsxEmit.React,
                reactNamespace: "React",
                allowJs: true,
                typeRoots: ["node_modules/@types"],
                lib: ["dom", "dom.iterable", "es6"]
              });

              // Add React types for better JSX support
              monaco.languages.typescript.typescriptDefaults.addExtraLib(
                `declare namespace React {
                  interface ReactElement<P = any, T extends string | JSXElementConstructor<any> = string | JSXElementConstructor<any>> {
                    type: T;
                    props: P;
                    key: Key | null;
                  }
                  type JSXElementConstructor<P> = ((props: P) => ReactElement | null) | (new (props: P) => Component<P, any>);
                  interface Component<P = {}, S = {}, SS = any> extends ComponentLifecycle<P, S, SS> {}
                  interface ComponentLifecycle<P, S, SS = any> {}
                  type Key = string | number;
                  type Ref<T> = RefCallback<T> | RefObject<T> | null;
                  type RefCallback<T> = (instance: T | null) => void;
                  interface RefObject<T> {
                    readonly current: T | null;
                  }
                }`,
                'react.d.ts'
              );

              // Disable strict error checking for JSX/TSX files
              monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions({
                noSemanticValidation: false,
                noSyntaxValidation: false,
                noSuggestionDiagnostics: false,
                onlyVisible: false
              });

              monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
                noSemanticValidation: false,
                noSyntaxValidation: false,
                noSuggestionDiagnostics: false,
                onlyVisible: false
              });

              // Define custom CSS for highlighting
              const style = document.createElement('style');
              style.textContent = `
                .highlighted-code-range {
                  background-color: rgba(255, 255, 0, 0.1);
                  border-left: 3px solid #ffd700;
                }
                .highlighted-code-range-glyph {
                  background-color: #ffd700;
                }
                .highlighted-code-range-decoration {
                  background-color: #ffd700;
                }
                .highlighted-code-range-margin {
                  background-color: rgba(255, 215, 0, 0.1);
                }
              `;
              document.head.appendChild(style);
              try {
                  monaco.editor.defineTheme('dracula', theme as any);
                  monaco.editor.setTheme('dracula');
              } catch (error) {
                console.error("Error loading Dracula theme:", error);
              }
            }}
            options={{
              fontSize: 14,
              fontFamily: "'Cascadia Code', 'Fira Code', 'Operator Mono', 'Consolas', 'Courier New', monospace",
              minimap: { enabled: true },
              scrollBeyondLastLine: false,
              automaticLayout: true,
              tabSize: 2,
              wordWrap: 'on',
              lineNumbers: 'on',
              folding: true,
              contextmenu: true,
              scrollbar: {
                verticalScrollbarSize: 10,
                horizontalScrollbarSize: 10,
                useShadows: false,
              },
              // Additional options to reduce unnecessary errors
              quickSuggestions: {
                other: true,
                comments: false,
                strings: true
              },
              suggestOnTriggerCharacters: true,
              acceptSuggestionOnCommitCharacter: true,
              acceptSuggestionOnEnter: 'on',
              wordBasedSuggestions: 'allDocuments',
              parameterHints: {
                enabled: true
              },
              hover: {
                enabled: true
              }
            }}
          />
        ) : (
          <div className="no-file-selected">
            Select a file to view or open one from the explorer.
          </div>
        )}
      </div>
    </div>
  );
};

export default FuturisticCodeEditor;
